import { Appointment, DashboardProgressStepRow, ProgressStep } from "../types";

export type ProgressStepStatus = "completed" | "in_progress" | "pending";

export function getProgressStepStatus(step: ProgressStep): ProgressStepStatus {
  if (step.endTime) return "completed";
  if (step.startTime) return "in_progress";
  return "pending";
}

/** Duration in ms: completed = end-start, in progress = now-start, pending = 0 */
export function getProgressStepDurationMs(step: ProgressStep, now: Date): number {
  if (step.endTime && step.startTime) {
    return new Date(step.endTime).getTime() - new Date(step.startTime).getTime();
  }
  if (step.startTime && !step.endTime) {
    return now.getTime() - new Date(step.startTime).getTime();
  }
  return 0;
}

export function formatProgressDuration(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

export function filterProgressStepsByProfessional(
  steps: ProgressStep[] | undefined,
  professionalId?: number,
): ProgressStep[] {
  if (!steps?.length) return [];
  if (professionalId == null) return steps;

  return steps.filter((step) =>
    step.professionals?.some((professional) => professional.id === professionalId),
  );
}

export function sumProgressStepDurationMs(
  steps: ProgressStep[],
  now: Date = new Date(),
): number {
  return steps.reduce(
    (total, step) => total + getProgressStepDurationMs(step, now),
    0,
  );
}

export function sumProgressStepMinutes(
  steps: ProgressStep[],
  now: Date = new Date(),
): number {
  return Math.round((sumProgressStepDurationMs(steps, now) / 60000) * 100) / 100;
}

export function appointmentHasActiveProgress(appointment: Appointment): boolean {
  return (appointment.progressSteps ?? []).some((s) => s.startTime && !s.endTime);
}

/** Active progress first, then by date + start time */
export function sortAppointmentsForEmployeeBoard(appointments: Appointment[]): Appointment[] {
  return [...appointments].sort((x, y) => {
    const ax = appointmentHasActiveProgress(x) ? 0 : 1;
    const ay = appointmentHasActiveProgress(y) ? 0 : 1;
    if (ax !== ay) return ax - ay;
    const dx = `${x.appointmentDate}T${x.startTime || "99:99"}`;
    const dy = `${y.appointmentDate}T${y.startTime || "99:99"}`;
    return dx.localeCompare(dy);
  });
}

function normalizeDashboardProgressStep(row: DashboardProgressStepRow): ProgressStep {
  const priceRaw = row.serviceSubService?.price;
  const price =
    typeof priceRaw === "number"
      ? priceRaw
      : parseFloat(String(priceRaw ?? "0")) || 0;

  return {
    id: row.id,
    notes: row.notes ?? "",
    startTime: row.startTime,
    endTime: row.endTime,
    serviceSubService: {
      id: row.serviceSubService.id,
      isRequired: row.serviceSubService.isRequired,
      price,
      subService: {
        id: row.serviceSubService.subService.id,
        name: row.serviceSubService.subService.name,
        description: row.serviceSubService.subService.description,
      },
    },
    professionals: row.professionals ?? [],
  };
}

/** Groups flat progress-step rows from GET /dashboard/progress-steps into appointments. */
export function groupDashboardProgressStepsIntoAppointments(
  rows: DashboardProgressStepRow[],
): Appointment[] {
  const grouped = new Map<
    number,
    { appointment: DashboardProgressStepRow["appointment"]; steps: ProgressStep[] }
  >();

  for (const row of rows) {
    const apt = row.appointment;
    if (!apt) continue;

    const step = normalizeDashboardProgressStep(row);

    if (!grouped.has(apt.id)) {
      grouped.set(apt.id, { appointment: apt, steps: [] });
    }
    grouped.get(apt.id)!.steps.push(step);
  }

  return Array.from(grouped.values()).map(({ appointment: apt, steps }) => {
    const sortedSteps = [...steps].sort((a, b) => a.id - b.id);
    const service = apt.service;

    return {
      id: apt.id,
      clientId: apt.clientId,
      client: apt.client,
      clientCarId: apt.clientCarId ?? undefined,
      clientCar: apt.clientCar ?? undefined,
      professionalId: 0,
      serviceId: apt.serviceId,
      appointmentDate: apt.appointmentDate,
      startTime: "",
      endTime: "",
      price: service.price ?? "0",
      status: apt.status,
      isRecurrency: false,
      updatedAt: "",
      createdAt: "",
      service: {
        description: "",
        name: service.name,
        duration: service.duration ?? 0,
        price: service.price ?? "0",
        type: service.type ?? "time",
        companyId: service.companyId ?? 0,
      },
      progressSteps: sortedSteps,
    };
  });
}
